import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataReplicationApi, MatterSearchType, BillSearchType, BankAccountSearchType, BankTransactionSearchType } from '../api/dataReplicationApi';
import { showToast } from '@repo/ui';

const QUERY_KEYS = {
  mattersMetadata: ['data-replication', 'matters', 'metadata'] as const,
  mattersCount: ['data-replication', 'matters', 'count'] as const,
  contactsMetadata: ['data-replication', 'contacts', 'metadata'] as const,
  contactsCount: ['data-replication', 'contacts', 'count'] as const,
  billsMetadata: ['data-replication', 'bills', 'metadata'] as const,
  billsCount: ['data-replication', 'bills', 'count'] as const,
  bankAccountsMetadata: ['data-replication', 'bank-accounts', 'metadata'] as const,
  bankAccountsCount: ['data-replication', 'bank-accounts', 'count'] as const,
  bankTransactionsMetadata: ['data-replication', 'bank-transactions', 'metadata'] as const,
  bankTransactionsCount: ['data-replication', 'bank-transactions', 'count'] as const,
  activitiesMetadata: ['data-replication', 'activities', 'metadata'] as const,
  activitiesCount: ['data-replication', 'activities', 'count'] as const,
  activityDescriptionsMetadata: ['data-replication', 'activity-descriptions', 'metadata'] as const,
  expenseCategoriesMetadata: ['data-replication', 'expense-categories', 'metadata'] as const,
  tasksMetadata: ['data-replication', 'tasks', 'metadata'] as const,
  usersMetadata: ['data-replication', 'users', 'metadata'] as const,
  lineItemsMetadata: ['data-replication', 'line-items', 'metadata'] as const,
  allocationsMetadata: ['data-replication', 'allocations', 'metadata'] as const,
  utbmsCodesMetadata: ['data-replication', 'utbms-codes', 'metadata'] as const,
  utbmsSetsMetadata: ['data-replication', 'utbms-sets', 'metadata'] as const,
  searchDynamoDB: (value: string, type: string) => ['data-replication', 'search', 'dynamodb', value, type] as const,
  searchClio: (value: string, type: string) => ['data-replication', 'search', 'clio', value, type] as const,
};

/**
 * Hook to get matters replication metadata
 */
export function useMattersReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.mattersMetadata,
    queryFn: () => dataReplicationApi.getMattersReplicationMetadata(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update matters replication metadata
 */
export function useUpdateMattersReplicationMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastReplication: string | null) =>
      dataReplicationApi.updateMattersReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mattersMetadata });
      showToast.success('Replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

/**
 * Hook to get contacts replication metadata
 */
export function useContactsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.contactsMetadata,
    queryFn: () => dataReplicationApi.getContactsReplicationMetadata(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update contacts replication metadata
 */
export function useUpdateContactsReplicationMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastReplication: string | null) =>
      dataReplicationApi.updateContactsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.contactsMetadata });
      showToast.success('Replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

/**
 * Hook to search for a matter in DynamoDB
 */
export function useSearchMatterDynamoDB(
  searchValue: string,
  searchType: MatterSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchDynamoDB(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchMatterInDynamoDB(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to search for a matter in Clio
 */
export function useSearchMatterClio(
  searchValue: string,
  searchType: MatterSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchClio(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchMatterInClio(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to sync a matter from Clio
 */
export function useSyncMatterFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (matterId: string) => dataReplicationApi.syncMatterFromClio(matterId),
    onSuccess: (data) => {
      // Invalidate all search queries and metadata
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.mattersMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Matter synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync matter: ${error.message}`);
    },
  });
}

/**
 * Hook to sync a contact from Clio
 */
export function useSyncContactFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) => dataReplicationApi.syncContactFromClio(contactId),
    onSuccess: (data) => {
      // Invalidate all search queries and metadata
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.contactsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Contact synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync contact: ${error.message}`);
    },
  });
}

// ===========================
// BILLS HOOKS
// ===========================

/**
 * Hook to get bills replication metadata
 */
export function useBillsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.billsMetadata,
    queryFn: () => dataReplicationApi.getBillsReplicationMetadata(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update bills replication metadata
 */
export function useUpdateBillsReplicationMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastReplication: string | null) =>
      dataReplicationApi.updateBillsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.billsMetadata });
      showToast.success('Bills replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update bills metadata: ${error.message}`);
    },
  });
}

/**
 * Hook to search for a bill in DynamoDB
 */
export function useSearchBillDynamoDB(
  searchValue: string,
  searchType: BillSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchDynamoDB(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchBillInDynamoDB(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to search for a bill in Clio
 */
export function useSearchBillClio(
  searchValue: string,
  searchType: BillSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchClio(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchBillInClio(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to sync a bill from Clio
 */
export function useSyncBillFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (billId: string) => dataReplicationApi.syncBillFromClio(billId),
    onSuccess: (data) => {
      // Invalidate all search queries and metadata
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.billsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Bill synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync bill: ${error.message}`);
    },
  });
}

// ===========================
// BANK ACCOUNTS HOOKS
// ===========================

/**
 * Hook to get bank accounts replication metadata
 */
export function useBankAccountsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.bankAccountsMetadata,
    queryFn: () => dataReplicationApi.getBankAccountsReplicationMetadata(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update bank accounts replication metadata
 */
export function useUpdateBankAccountsReplicationMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastReplication: string | null) =>
      dataReplicationApi.updateBankAccountsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankAccountsMetadata });
      showToast.success('Bank accounts replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update bank accounts metadata: ${error.message}`);
    },
  });
}

/**
 * Hook to search for a bank account in DynamoDB
 */
export function useSearchBankAccountDynamoDB(
  searchValue: string,
  searchType: BankAccountSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchDynamoDB(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchBankAccountInDynamoDB(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to search for a bank account in Clio
 */
export function useSearchBankAccountClio(
  searchValue: string,
  searchType: BankAccountSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchClio(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchBankAccountInClio(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to sync a bank account from Clio
 */
export function useSyncBankAccountFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => dataReplicationApi.syncBankAccountFromClio(accountId),
    onSuccess: (data) => {
      // Invalidate all search queries and metadata
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankAccountsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Bank account synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync bank account: ${error.message}`);
    },
  });
}

// ===========================
// BANK TRANSACTIONS HOOKS
// ===========================

/**
 * Hook to get bank transactions replication metadata
 */
export function useBankTransactionsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.bankTransactionsMetadata,
    queryFn: () => dataReplicationApi.getBankTransactionsReplicationMetadata(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update bank transactions replication metadata
 */
export function useUpdateBankTransactionsReplicationMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastReplication: string | null) =>
      dataReplicationApi.updateBankTransactionsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankTransactionsMetadata });
      showToast.success('Bank transactions replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update bank transactions metadata: ${error.message}`);
    },
  });
}

/**
 * Hook to search for a bank transaction in DynamoDB
 */
export function useSearchBankTransactionDynamoDB(
  searchValue: string,
  searchType: BankTransactionSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchDynamoDB(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchBankTransactionInDynamoDB(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to search for a bank transaction in Clio
 */
export function useSearchBankTransactionClio(
  searchValue: string,
  searchType: BankTransactionSearchType,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: QUERY_KEYS.searchClio(searchValue, searchType),
    queryFn: () => dataReplicationApi.searchBankTransactionInClio(searchValue, searchType),
    enabled: enabled && !!searchValue && !!searchType,
    staleTime: 0, // Always fetch fresh data for searches
  });
}

/**
 * Hook to sync a bank transaction from Clio
 */
export function useSyncBankTransactionFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: string) => dataReplicationApi.syncBankTransactionFromClio(transactionId),
    onSuccess: (data) => {
      // Invalidate all search queries and metadata
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankTransactionsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Bank transaction synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync bank transaction: ${error.message}`);
    },
  });
}

/**
 * Hook to preview bank transactions for a matter
 */
export function usePreviewMatterBankTransactions() {
  return useMutation({
    mutationFn: ({ 
      matterId, 
      options 
    }: { 
      matterId: string; 
      options?: { dateFrom?: string; dateTo?: string; limit?: number } 
    }) => dataReplicationApi.previewMatterBankTransactions(matterId, options),
    onError: (error: Error) => {
      showToast.error(`Failed to preview matter transactions: ${error.message}`);
    },
  });
}

/**
 * Hook to sync bank transactions for a matter
 */
export function useSyncMatterBankTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      matterId, 
      options 
    }: { 
      matterId: string; 
      options?: { dateFrom?: string; dateTo?: string; limit?: number } 
    }) => dataReplicationApi.syncMatterBankTransactions(matterId, options),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankTransactionsMetadata });
      showToast.success(data.message || 'Matter bank transactions synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync matter transactions: ${error.message}`);
    },
  });
}

/**
 * Hook to preview bank transactions for a bank account
 */
export function usePreviewBankAccountTransactions() {
  return useMutation({
    mutationFn: ({ 
      bankAccountId, 
      options 
    }: { 
      bankAccountId: string; 
      options?: { dateFrom?: string; dateTo?: string; matterId?: string; limit?: number } 
    }) => dataReplicationApi.previewBankAccountTransactions(bankAccountId, options),
    onError: (error: Error) => {
      showToast.error(`Failed to preview bank account transactions: ${error.message}`);
    },
  });
}

/**
 * Hook to sync bank transactions for a bank account
 */
export function useSyncBankAccountTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      bankAccountId, 
      options 
    }: { 
      bankAccountId: string; 
      options?: { dateFrom?: string; dateTo?: string; matterId?: string; limit?: number } 
    }) => dataReplicationApi.syncBankAccountTransactions(bankAccountId, options),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.bankTransactionsMetadata });
      showToast.success(data.message || 'Bank account transactions synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync bank account transactions: ${error.message}`);
    },
  });
}

// ===================================
// ACTIVITIES REPLICATION HOOKS
// ===================================

/**
 * Hook to get activities replication metadata
 */
export function useActivitiesReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.activitiesMetadata,
    queryFn: () => dataReplicationApi.getActivitiesReplicationMetadata(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to update activities replication metadata
 */
export function useUpdateActivitiesReplicationMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastReplication: string | null) =>
      dataReplicationApi.updateActivitiesReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activitiesMetadata });
      showToast.success('Activities replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update activities metadata: ${error.message}`);
    },
  });
}

/**
 * Hook to search for an activity in DynamoDB
 */
export function useSearchActivityDynamoDB(
  searchValue: string,
  searchType: 'activityId' | 'matterId' | 'billId',
  enabled = false
) {
  return useQuery({
    queryKey: ['data-replication', 'search', 'activities', 'dynamodb', searchValue, searchType],
    queryFn: () => dataReplicationApi.searchActivityInDynamoDB(searchValue, searchType),
    enabled: enabled && !!searchValue,
    staleTime: 0, // Always fetch fresh data
  });
}

/**
 * Hook to search for an activity in Clio
 */
export function useSearchActivityClio(
  searchValue: string,
  searchType: 'activityId',
  enabled = false
) {
  return useQuery({
    queryKey: ['data-replication', 'search', 'activities', 'clio', searchValue, searchType],
    queryFn: () => dataReplicationApi.searchActivityInClio(searchValue, searchType),
    enabled: enabled && !!searchValue,
    staleTime: 0, // Always fetch fresh data
  });
}

/**
 * Hook to sync an activity from Clio to DynamoDB
 */
export function useSyncActivityFromClio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: string) => dataReplicationApi.syncActivityFromClio(activityId),
    onSuccess: (data) => {
      // Invalidate all search queries and metadata
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activitiesMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Activity synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync activity: ${error.message}`);
    },
  });
}

// ===================================
// ACTIVITY DESCRIPTIONS HOOKS
// ===================================

export function useActivityDescriptionsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.activityDescriptionsMetadata,
    queryFn: () => dataReplicationApi.getActivityDescriptionsReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateActivityDescriptionsReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateActivityDescriptionsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activityDescriptionsMetadata });
      showToast.success('Activity Descriptions replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncActivityDescriptionFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (activityDescriptionId: string) => dataReplicationApi.syncActivityDescriptionFromClio(activityDescriptionId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.activityDescriptionsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Activity Description synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync activity description: ${error.message}`);
    },
  });
}

// ===================================
// EXPENSE CATEGORIES HOOKS
// ===================================

export function useExpenseCategoriesReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.expenseCategoriesMetadata,
    queryFn: () => dataReplicationApi.getExpenseCategoriesReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateExpenseCategoriesReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateExpenseCategoriesReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenseCategoriesMetadata });
      showToast.success('Expense Categories replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncExpenseCategoryFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expenseCategoryId: string) => dataReplicationApi.syncExpenseCategoryFromClio(expenseCategoryId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.expenseCategoriesMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Expense Category synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync expense category: ${error.message}`);
    },
  });
}

// ===================================
// TASKS HOOKS
// ===================================

export function useTasksReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.tasksMetadata,
    queryFn: () => dataReplicationApi.getTasksReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateTasksReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateTasksReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasksMetadata });
      showToast.success('Tasks replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncTaskFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => dataReplicationApi.syncTaskFromClio(taskId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasksMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Task synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync task: ${error.message}`);
    },
  });
}

// ===================================
// USERS HOOKS
// ===================================

export function useUsersReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.usersMetadata,
    queryFn: () => dataReplicationApi.getUsersReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateUsersReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateUsersReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.usersMetadata });
      showToast.success('Users replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncUserFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => dataReplicationApi.syncUserFromClio(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.usersMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'User synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync user: ${error.message}`);
    },
  });
}

// ===================================
// LINE ITEMS HOOKS
// ===================================

export function useLineItemsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.lineItemsMetadata,
    queryFn: () => dataReplicationApi.getLineItemsReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateLineItemsReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateLineItemsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lineItemsMetadata });
      showToast.success('Line Items replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncLineItemFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lineItemId: string) => dataReplicationApi.syncLineItemFromClio(lineItemId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lineItemsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Line Item synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync line item: ${error.message}`);
    },
  });
}

// ===================================
// ALLOCATIONS HOOKS
// ===================================

export function useAllocationsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.allocationsMetadata,
    queryFn: () => dataReplicationApi.getAllocationsReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateAllocationsReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateAllocationsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allocationsMetadata });
      showToast.success('Allocations replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncAllocationFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (allocationId: string) => dataReplicationApi.syncAllocationFromClio(allocationId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allocationsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'Allocation synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync allocation: ${error.message}`);
    },
  });
}

// ===================================
// UTBMS CODES HOOKS
// ===================================

export function useUtbmsCodesReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.utbmsCodesMetadata,
    queryFn: () => dataReplicationApi.getUtbmsCodesReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateUtbmsCodesReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateUtbmsCodesReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.utbmsCodesMetadata });
      showToast.success('UTBMS Codes replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncUtbmsCodeFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (utbmsCodeId: string) => dataReplicationApi.syncUtbmsCodeFromClio(utbmsCodeId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.utbmsCodesMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'UTBMS Code synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync UTBMS code: ${error.message}`);
    },
  });
}

// ===================================
// UTBMS SETS HOOKS
// ===================================

export function useUtbmsSetsReplicationMetadata() {
  return useQuery({
    queryKey: QUERY_KEYS.utbmsSetsMetadata,
    queryFn: () => dataReplicationApi.getUtbmsSetsReplicationMetadata(),
    staleTime: 30 * 1000,
  });
}

export function useUpdateUtbmsSetsReplicationMetadata() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lastReplication: string | null) => dataReplicationApi.updateUtbmsSetsReplicationMetadata(lastReplication),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.utbmsSetsMetadata });
      showToast.success('UTBMS Sets replication metadata updated successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to update metadata: ${error.message}`);
    },
  });
}

export function useSyncUtbmsSetFromClio() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (utbmsSetId: string) => dataReplicationApi.syncUtbmsSetFromClio(utbmsSetId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.utbmsSetsMetadata });
      queryClient.invalidateQueries({ queryKey: ['data-replication', 'search'] });
      showToast.success(data.message || 'UTBMS Set synced successfully');
    },
    onError: (error: Error) => {
      showToast.error(`Failed to sync UTBMS set: ${error.message}`);
    },
  });
}
